import React from 'react';
import { Badge } from 'reactstrap';
import { useSelector } from 'react-redux';

import JsonDisplay from '../../components/JsonDisplay';
import TierBadge from '../../components/TierBadge';
import { imgUrl } from '../../utils/api-request';
import { getOrderActionConfig, getEventTypeConfig } from './constants';

import './CronjobNotifications.scss';
import ClickToCopy from '../../components/ClickToCopy';

const MetadataCard = ({ icon, label, children }) => (
    <div className="metadata-card">
        <div className="text-white-50 small mb-1">
            <span className={`fas ${icon} me-1`} />
            {label}
        </div>
        <div>{children}</div>
    </div>
);

const ORDER_FLAGS = [
    { key: 'orderCreated', label: 'Created' },
    { key: 'orderUpdated', label: 'Updated' },
    { key: 'orderSyncedFromCloud', label: 'Synced From Cloud' },
    { key: 'orderDeprecated', label: 'Deprecated' }
];

const BOOL_DIAGNOSTICS = [
    { key: 'countryMatch', label: 'Territory Match' },
    { key: 'countryTierMatch', label: 'Territory Tier Match' },
    { key: 'organizationInAvailableForOrganizations', label: 'Org In Available List' },
    { key: 'isOrganizationMatching', label: 'Organization Matching' },
    { key: 'moviePublished', label: 'Movie Published' }
];

const BoolIcon = ({ value }) => (
    <span className={`fas ${value ? 'fa-circle-check text-success' : 'fa-circle-xmark text-danger'}`} />
);

const CronjobNotificationDetailPanel = ({ onClose }) => {
    const { selectedNotification: notification, selectedStatus } = useSelector(state => state.cronjobNotifications);

    if (!notification && selectedStatus !== 'loading') {
        return null;
    }

    if (selectedStatus === 'loading') {
        return (
            <div className="cronjob-notification-panel bg-broken shadow-lg">
                <div className="cronjob-notification-panel-header">
                    <h5 className="mb-0">Loading...</h5>
                    <button className="btn btn-link text-white p-0" onClick={onClose}>
                        <span className="fas fa-times fa-lg" />
                    </button>
                </div>
                <div className="cronjob-notification-panel-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const {
        organization,
        organizationName,
        eventType,
        movieversionId,
        movieversionUuid,
        orderId,
        orderAction,
        entity,
        action,
        details,
        auditLogs,
        cronjob,
        source,
        createdAt,
        updatedAt
    } = notification;

    const payload = notification.payload || {};
    const actionConfig = getOrderActionConfig(orderAction);
    const eventConfig = getEventTypeConfig(eventType);

    // organization and movieversionId arrive populated (or null for deleted
    // refs); guard for raw ids so stale store data can't crash the render
    const organizationObj = organization?.name ? organization : null;
    const organizationId = organizationObj?._id
        || (typeof organization === 'string' ? organization : null)
        || notification.organization_id;
    const movieversion = movieversionId?.name ? movieversionId : null;
    const movie = movieversion?.movie?.title ? movieversion.movie : null;
    const movieversionIdValue = movieversion?._id
        || (typeof movieversionId === 'string' ? movieversionId : null);

    const movieversionCountries = payload.movieversionCountries || [];
    const movieversionTier = payload.movieversionTier || [];
    const changedFields = payload.changedFields || [];
    const countryMatches = movieversionCountries.includes(payload.organizationCountry);
    const tierMatches = movieversionTier.includes(payload.organizationTier);

    return (
        <div className="cronjob-notification-panel bg-broken shadow-lg">
            {/* Header */}
            <div className="cronjob-notification-panel-header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Badge color={actionConfig.color} style={{ borderRadius: 0 }}>
                        <span className={`fas ${actionConfig.icon} me-1`} />
                        {actionConfig.label}
                    </Badge>
                    <Badge color={eventConfig.color} style={{ borderRadius: 0 }}>
                        <span className={`fas ${eventConfig.icon} me-1`} />
                        {eventConfig.label}
                    </Badge>
                </div>
                <button className="btn btn-link text-white p-0" onClick={onClose}>
                    <span className="fas fa-times fa-lg" />
                </button>
            </div>

            {/* Body */}
            <div className="cronjob-notification-panel-body">
                {/* Details Banner */}
                {details && (
                    <div className={`details-banner action-${actionConfig.color} mb-4`}>
                        <p className="mb-0">{details}</p>
                    </div>
                )}

                {/* Metadata Grid */}
                <div className="metadata-grid mb-4">
                    <MetadataCard icon="fa-building" label="Organization">
                        <div>{organizationObj?.name || organizationName || '—'}</div>
                        {organizationId && <code className="small text-white-50">{organizationId}</code>}
                    </MetadataCard>

                    <MetadataCard icon="fa-film" label="Movieversion">
                        <div>{movieversion?.name || payload.movieversionName || '—'}</div>
                        {movieversionUuid ? (
                            <code className="small text-white-50">{movieversionUuid}</code>
                        ) : (
                            movieversionIdValue && <code className="small text-white-50">{movieversionIdValue}</code>
                        )}
                    </MetadataCard>

                    <MetadataCard icon="fa-calendar" label="Recorded At">
                        {new Date(createdAt).toLocaleString()}
                    </MetadataCard>

                    {updatedAt && updatedAt !== createdAt && (
                        <MetadataCard icon="fa-clock" label="Updated At">
                            {new Date(updatedAt).toLocaleString()}
                        </MetadataCard>
                    )}

                    <MetadataCard icon="fa-robot" label="Source">
                        <code>{source || '—'}</code>
                        {cronjob && (
                            <Badge color="secondary" className="ms-2" style={{ borderRadius: 0 }}>
                                cronjob
                            </Badge>
                        )}
                    </MetadataCard>

                    <MetadataCard icon="fa-clipboard-list" label="Order">
                        {orderId ? <code className="small">{orderId}</code> : '—'}
                    </MetadataCard>

                    <MetadataCard icon="fa-tag" label="Entity / Action">
                        <div>{entity || '—'}</div>
                        <code className="small text-white-50">{action || '—'}</code>
                    </MetadataCard>
                </div>

                {/* Movie & Version */}
                {movie && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-film me-1" />
                            Movie
                        </div>
                        <div className="d-flex gap-3 align-items-start p-3 bg-black/30 rounded">
                            <div className="panel-poster flex-shrink-0">
                                {movie.poster?.imageUrl ? (
                                    <img src={imgUrl(movie.poster.imageUrl)} alt={movie.title} />
                                ) : (
                                    <div className="panel-poster-placeholder d-flex align-items-center justify-content-center">
                                        <span className="fas fa-film fa-2x text-white-50" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-grow-1 overflow-hidden">
                                <div className="text-uppercase text-primary fw-bold mb-1">
                                    {movie.title}
                                    {movie.releaseYear && (
                                        <span className="badge bg-primary text-black ms-2">{movie.releaseYear}</span>
                                    )}
                                </div>
                                {movie.status && (
                                    <div className="small text-white-50 mb-2">
                                        <span className={`fas fa-fw ${movie.status === 'published' ? 'fa-check text-success' : 'fa-pencil text-danger'} me-1`} />
                                        {movie.status}
                                    </div>
                                )}
                                <div className="mb-2">
                                    <span className="fas fa-clapperboard me-2 text-primary" />
                                    {movieversion.name || payload.movieversionName || '—'}
                                    {movieversion.isDeleted && (
                                        <Badge color="danger" className="ms-2" style={{ borderRadius: 0 }}>
                                            deleted
                                        </Badge>
                                    )}
                                </div>
                                {movieversion.uuid && (
                                    <code className="small text-white-50 d-block mb-2"><ClickToCopy text={movieversion.uuid} /></code>
                                )}
                                {movieversion.tier?.length > 0 && (
                                    <div className="d-flex gap-2 flex-wrap mb-2">
                                        {movieversion.tier.map(tier => (
                                            <TierBadge key={tier} tier={tier} small />
                                        ))}
                                    </div>
                                )}
                                {movieversion.countries?.length > 0 && (
                                    <div className="d-flex gap-1 flex-wrap">
                                        {movieversion.countries.map(country => (
                                            <Badge key={country} color="secondary" style={{ borderRadius: 0 }}>
                                                {country}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Order Flags */}
                <div className="mb-4">
                    <div className="text-white-50 small mb-2">
                        <span className="fas fa-clipboard-check me-1" />
                        Order Flags
                    </div>
                    <div className="d-flex gap-3 flex-wrap">
                        {ORDER_FLAGS.map(({ key, label }) => (
                            <div key={key} className="d-flex align-items-center gap-2 p-2 bg-black/30 rounded small">
                                <BoolIcon value={!!notification[key]} />
                                {label}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Matching Diagnostics */}
                <div className="mb-4">
                    <div className="text-white-50 small mb-2">
                        <span className="fas fa-stethoscope me-1" />
                        Matching Diagnostics
                    </div>
                    <div className={`p-3 bg-black/30 rounded border-start border-3 border-${payload.isOrganizationMatching ? 'success' : 'danger'}`}>
                        <div className="row g-2 mb-3">
                            {BOOL_DIAGNOSTICS.map(({ key, label }) => (
                                <div key={key} className="col-6 d-flex align-items-center gap-2">
                                    <BoolIcon value={!!payload[key]} />
                                    {label}
                                </div>
                            ))}
                        </div>

                        <div className="metadata-grid mb-3">
                            <MetadataCard icon="fa-flag" label="Organization Territory">
                                <Badge color={countryMatches ? 'success' : 'danger'} style={{ borderRadius: 0 }}>
                                    {payload.organizationCountry || '—'}
                                </Badge>
                            </MetadataCard>

                            <MetadataCard icon="fa-earth-europe" label="Movieversion Countries">
                                {movieversionCountries.length > 0 ? (
                                    <div className="d-flex gap-1 flex-wrap">
                                        {movieversionCountries.map(country => (
                                            <Badge
                                                key={country}
                                                color={country === payload.organizationCountry ? 'success' : 'secondary'}
                                                style={{ borderRadius: 0 }}
                                            >
                                                {country}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : '—'}
                            </MetadataCard>

                            <MetadataCard icon="fa-layer-group" label="Organization Tier">
                                <Badge color={tierMatches ? 'success' : 'danger'} style={{ borderRadius: 0 }}>
                                    {payload.organizationTier || '—'}
                                </Badge>
                            </MetadataCard>

                            <MetadataCard icon="fa-layer-group" label="Movieversion Tiers">
                                {movieversionTier.length > 0 ? (
                                    <div className="d-flex gap-1 flex-wrap">
                                        {movieversionTier.map(tier => (
                                            <Badge
                                                key={tier}
                                                color={tier === payload.organizationTier ? 'success' : 'secondary'}
                                                style={{ borderRadius: 0 }}
                                            >
                                                {tier}
                                            </Badge>
                                        ))}
                                    </div>
                                ) : '—'}
                            </MetadataCard>

                            <MetadataCard icon="fa-circle-info" label="Movie Status">
                                {payload.movieStatus || '—'}
                            </MetadataCard>

                            <MetadataCard icon="fa-file-invoice" label="Order Type / Status">
                                <div>{payload.orderType || '—'}</div>
                                <code className="small text-white-50">{payload.orderStatus || '—'}</code>
                            </MetadataCard>
                        </div>

                        {changedFields.length > 0 && (
                            <div>
                                <div className="text-white-50 small mb-2">Changed Fields</div>
                                <div className="d-flex gap-1 flex-wrap">
                                    {changedFields.map(field => (
                                        <Badge key={field} color="secondary" style={{ borderRadius: 0 }}>
                                            {field}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Full Payload */}
                {Object.keys(payload).length > 0 && (
                    <div className="mb-4">
                        <JsonDisplay
                            data={payload}
                            title="Payload"
                            defaultOpen={false}
                        />
                    </div>
                )}

                {/* Audit Logs */}
                {auditLogs?.length > 0 && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-list-check me-1" />
                            Audit Logs
                        </div>
                        {auditLogs.map((log, index) => (
                            <div key={index} className="mb-2">
                                <JsonDisplay
                                    data={log}
                                    title={`Audit Log #${index + 1}`}
                                    defaultOpen={false}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CronjobNotificationDetailPanel;
